/*
  * Stores the action-creators, helpers, and higher-level functions which describe the flow of
  * fetching/caching CARTO sql API data from a dataset, batched by US states.
 */

import DataManager from '../../DataManagers/DataManager'
import {
	cartoLayerIdOfSelectedGeographySelector,
	selectedRegionACSDataRowSelector,
	cartoACSDataSelector,
	errorMessagesSelector,
	arrayOfUSStatesLoadingACSDataSelector,
	selectedUSStatesToFilterWithinSelector,
	isLoadingCartoExtentsSelector,
	ACSDataForStatesSelector,
	_turfManagerSelector
} from "../selectors";
import {
	_updateURLWithCurrentState, updateSelectedRegionToNewGeography,
	doFieldFilterBoundsUpdateInMapRedux,
	mapFitBounds,
	saveFieldExtentsFromCarto,
	toggleButtonToggleStatusArrayForField, toggleHasBeenEditedByTheUserProp, updateToYamlSpecifiedFiltersAndButtonsIfAny
} from "./mapActions";
import {fieldExtentsSelector} from "../selectors";
import {DEFAULT_FILTER_BOUNDS_OBJECT_PROPERTIES} from "../Reducers/mapReducer";
import {fieldFilterBoundsSelector} from "../selectors";
import {onlyUnique} from "../../utils";
import {mergeInUpdatedFieldBounds} from "../../DataManagers/utilsData";
import {updateFilterFieldBeingManuallyEditedAction} from "./uiActions";

export const DATA_STORAGE_TYPES = {
	summary: 'Summary',
	extents: 'Extents',
	data: 'Data',
}

// Action creators (return actions)

export const START_FETCH_ACTION = 'START_FETCH_ACTION'
const startFetchAction = (dataStorageType) => ({
	type: START_FETCH_ACTION,
	loadingKeyName: `isLoadingCarto${dataStorageType}`
})

export const END_FETCH_ACTION = 'END_FETCH_ACTION'
const endFetchAction = (dataStorageType) => ({
	type: END_FETCH_ACTION,
	loadingKeyName: `isLoadingCarto${dataStorageType}`
})

export const START_ACS_DATA_FETCH_ACTION = 'START_ACS_DATA_FETCH_ACTION'
const startFetchACSDataAction = (stateName) => ({
	type: START_ACS_DATA_FETCH_ACTION,
	stateName,
})

export const END_ACS_DATA_FETCH_ACTION = 'END_ACS_DATA_FETCH_ACTION'
const endFetchACSDataAction = (stateName) => ({
	type: END_ACS_DATA_FETCH_ACTION,
	stateName,
})

export const HANDLE_FETCH_ERROR_ACTION = 'HANDLE_FETCH_ERROR_ACTION'
const handleFetchErrorAction = (errorMessage, dataStorageType) => ({
	type: HANDLE_FETCH_ERROR_ACTION,
	errorMessage: errorMessage || true,
	dataStorageType,
})

export const SAVE_DATA_ACTION = 'SAVE_DATA_ACTION'
const saveDataAction = (stateName, cartoLayerId, response) => ({
	type: SAVE_DATA_ACTION,
	stateName: stateName,
	cartoLayerId: cartoLayerId,
	data: response[0].data.rows
})

export const SAVE_SUMMARY_ACTION = 'SAVE_SUMMARY_ACTION'
const saveSummaryAction = (cartoLayerId, response) => ({
	type: SAVE_SUMMARY_ACTION,
	cartoLayerId: cartoLayerId,
	data: response[0].data.rows
})

export const CHANGE_SELECTED_GEOGRAPHY = 'CHANGE_SELECTED_GEOGRAPHY'
const changeSelectedGeographyAction = (geography) => ({
	type: CHANGE_SELECTED_GEOGRAPHY,
	newSelectedGeography: geography,
})

export const INITIALIZE_CARTO_ACS_DATA_FROM_URL = 'INITIALIZE_CARTO_ACS_DATA_FROM_URL'
const initializeCartoACSDataFromURLAction = (stateUpdates) => ({
	type: INITIALIZE_CARTO_ACS_DATA_FROM_URL,
	stateUpdates: stateUpdates,
})

export const ADD_US_STATE_TO_FILTER_WITHIN_ACTION = 'ADD_US_STATE_TO_FILTER_WITHIN_ACTION'
const addUSStateToFilterWithinAction = (addedUSState) => ({
	type: ADD_US_STATE_TO_FILTER_WITHIN_ACTION,
	addedUSState: addedUSState,
})

export const UPDATE_US_STATE_TO_FILTER_WITHIN_ACTION = 'UPDATE_US_STATE_TO_FILTER_WITHIN_ACTION'
export const updateUSStatesToFilterWithinAction = (updatedSelectedUSStatesToFilterWithin) => ({
	type: UPDATE_US_STATE_TO_FILTER_WITHIN_ACTION,
	updatedSelectedUSStatesToFilterWithin,
})

// Helpers

const shouldFetchData = (getReduxState, stateName, cartoLayerId) => {
	const reduxState = getReduxState()
	const cartoACSData = cartoACSDataSelector(reduxState)
	const arrayOfUSStatesLoadingACSData = arrayOfUSStatesLoadingACSDataSelector(reduxState)
	const errorMessages = errorMessagesSelector(reduxState)
	
	const cachedDataForThisState = cartoACSData[cartoLayerId] && cartoACSData[cartoLayerId][stateName]
	const currentlyFetchingDataForThisState = arrayOfUSStatesLoadingACSData.includes(stateName)
	const errorMessage = errorMessages[DATA_STORAGE_TYPES.data]
	
	if (errorMessage) {
		alert(`An error occurred while we tried to fetch data for ${stateName}. If restarting the map didn't fix this, contact chris.rowe@ruralinnovation.us. Error: ${errorMessage}`)
	}
	
	return !(currentlyFetchingDataForThisState || cachedDataForThisState || errorMessage)
}

const getCartoAttributeDataPromiseFor = (stateName, cartoLayerId) => {
	return DataManager.stats.getCartoAttributeDataPromiseFor(stateName, cartoLayerId)
}

const getCartoSummaryPromiseFor = (cartoLayerId) => {
	return DataManager.stats.getCartoSummaryDataForVis(cartoLayerId)
}

const getCartoGeoIdLookupTablePromiseFor = (censusBlockFIPSCode) => {
	return DataManager.stats.getCartoGeoIdLookupTablePromiseFor(censusBlockFIPSCode)
}

const getCensusBlockFIPSCode = (selectedRegionInfo, onComplete) => {
	return DataManager.stats.getCensusBlockFIPSCodeFromLatLon(selectedRegionInfo, onComplete)
}

// Geocoder

export const getCartoGeoIdForLatLon = (selectedRegionInfo, onComplete) => {
	return (dispatch, getReduxState) => {
		getCensusBlockFIPSCode(selectedRegionInfo, censusBlockFIPSCode => {
			// fetch the lookup table row
			getCartoGeoIdLookupTablePromiseFor(censusBlockFIPSCode).then(response => {
				const _correspondingGeoIdsForNonFIPSGeos = response[0].data.rows[0]
				const correspondingGeoIdsForNonFIPSGeos = Object.keys(_correspondingGeoIdsForNonFIPSGeos).reduce((strFormatVersion, currentFieldName) => {
					strFormatVersion[currentFieldName] = String(_correspondingGeoIdsForNonFIPSGeos[currentFieldName])
					return strFormatVersion
				}, {})
				
				onComplete(correspondingGeoIdsForNonFIPSGeos)
			})
			
		})
	}
}


// we want to leave them Filter Bounds as untouched as possible for consistency across geography/location, but within the data's extent
// We can assume that both parameters are properly populated with valid numbers for all fields.
const getFieldFilterBoundsAdjustedWithinTheExtents = (fieldExtents, oldFieldFilterBounds) => {
	// if (Object.keys(oldFieldFilterBounds).length === 0) return fieldExtents

	for (const fieldName in oldFieldFilterBounds) {
		const [fieldMin, fieldMax] = fieldExtents[fieldName]
		const [filterMin, filterMax] = oldFieldFilterBounds[fieldName].filterBounds
		const isValid = (number) => (typeof number === 'number') && !Number.isNaN(number)

		const lowerBound = Math.max(fieldMin, isValid(filterMin) ? filterMin : fieldMin)
		const upperBound = Math.min(fieldMax, isValid(filterMax) ? filterMax : fieldMax)

		oldFieldFilterBounds[fieldName].filterBounds = [lowerBound, upperBound]
	}

	return oldFieldFilterBounds
}

const fetchCartoACSDataIfNeeded = (stateName) => {
	return (dispatch, getReduxState) => {
		const cartoLayerId = cartoLayerIdOfSelectedGeographySelector(getReduxState())
		if (shouldFetchData(getReduxState, stateName, cartoLayerId)) {
			//console.log('fetching data for:', stateName)
			dispatch(startFetchACSDataAction(stateName))
			return getCartoAttributeDataPromiseFor(stateName, cartoLayerId)
				.then(res => dispatch(saveDataAction(stateName, cartoLayerId, res)))
				.catch((e) => dispatch(handleFetchErrorAction('' + e, DATA_STORAGE_TYPES.data)))
				.finally(() => {
					dispatch(updateFieldFilterBounds())
					dispatch(endFetchACSDataAction(stateName))
					dispatch(updateToYamlSpecifiedFiltersAndButtonsIfAny())
				})
		} else { // Cache hit!
			dispatch(updateFieldFilterBounds())
			return Promise.resolve()
		}
	}
}

const fetchCartoACSSummaryIfNeeded = () => {
	return (dispatch, getReduxState) => {
		const cartoLayerId = cartoLayerIdOfSelectedGeographySelector(getReduxState())
		const cartoACSDataCache = getReduxState().cartoACSData
		if (!cartoACSDataCache.isLoadingCartoSummary && !(cartoACSDataCache[cartoLayerId] && cartoACSDataCache[cartoLayerId].summary)) {
			dispatch(startFetchAction(DATA_STORAGE_TYPES.summary))
			return getCartoSummaryPromiseFor(cartoLayerId)
				.then(res => dispatch(saveSummaryAction(cartoLayerId, res)))
				.catch((e) => dispatch(handleFetchErrorAction('' + e, DATA_STORAGE_TYPES.summary)))
				.finally(() => dispatch(endFetchAction(DATA_STORAGE_TYPES.summary)))
		} else {
			return Promise.resolve()
		}
	}
}

const fetchCartoACSExtentsIfNeeded = (geographyOrUSStateChanged = false) => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const cartoLayerId = cartoLayerIdOfSelectedGeographySelector(reduxState)
		const fieldExtents = fieldExtentsSelector(reduxState)
		const selectedUSStatesToFilterWithin = selectedUSStatesToFilterWithinSelector(reduxState)
		const isLoadingCartoExtents = isLoadingCartoExtentsSelector(reduxState)
		
		const cacheNeedsUpdating = geographyOrUSStateChanged || Object.keys(fieldExtents).length === 0
		
		if (selectedUSStatesToFilterWithin.length && !isLoadingCartoExtents && cacheNeedsUpdating) {
			dispatch(startFetchAction(DATA_STORAGE_TYPES.extents))
			return DataManager.stats.getCartoAttributeDataExtents(cartoLayerId, selectedUSStatesToFilterWithin)
				.then(res => {
					dispatch(updateFilterFieldBeingManuallyEditedAction(undefined))
					dispatch(saveFieldExtentsFromCarto(res))
					dispatch(updateFieldFilterBounds({resetToExtents: true}))
				})
				.catch((e) => dispatch(handleFetchErrorAction('' + e, DATA_STORAGE_TYPES.extents)))
				.finally(() => dispatch(endFetchAction(DATA_STORAGE_TYPES.extents)))
		} else {
			return Promise.resolve()
		}
	}
}

// Actions Called by UI: when asynchronous, return a Promise so UI can .then() method-chain if needed.


// THIS IS A WRAPPER. It fetches all data (multiple states  if needed
export const fetchCartoACSSummaryAndDataIfNeeded = (options = {}) => {
	return (dispatch, getReduxState) => {
		const {geographyOrUSStateChanged = false} = options
		dispatch(fetchCartoACSSummaryIfNeeded())
		dispatch(fetchCartoACSExtentsIfNeeded(geographyOrUSStateChanged))
		const reduxState = getReduxState()
		const selectedUSStatesToFilterWithin = selectedUSStatesToFilterWithinSelector(reduxState)
		selectedUSStatesToFilterWithin.forEach(stateName => {
			dispatch(fetchCartoACSDataIfNeeded(stateName))
		})
	}
}

export const changeSelectedGeography = (geography) => {
	return (dispatch, getReduxState) => {
		dispatch(changeSelectedGeographyAction(geography))
		dispatch(updateSelectedRegionToNewGeography())
		dispatch(fetchCartoACSSummaryAndDataIfNeeded({geographyOrUSStateChanged: true}))
		dispatch(_updateURLWithCurrentState())
	}
}

const getSimilarCommunitiesFilterBounds = (fieldExtentsForField, selectedRegionFieldValue, filterBoundsTheButtonsShouldToggleForButton) => {
	const percentToFilterBelowSelectedRegion = Number(filterBoundsTheButtonsShouldToggleForButton[0].replace('%', ''))
	const percentToFilterAboveSelectedRegion = Number(filterBoundsTheButtonsShouldToggleForButton[1].replace('%', ''))
	
	// Calculate bounds (within extents)
	const range = fieldExtentsForField[1] - fieldExtentsForField[0]
	const lowerBound = Math.max(fieldExtentsForField[0], selectedRegionFieldValue + (range * (percentToFilterBelowSelectedRegion / 100)))
	const upperBound = Math.min(fieldExtentsForField[1], selectedRegionFieldValue + (range * (percentToFilterAboveSelectedRegion / 100)))
	return [lowerBound, upperBound]
}

const calculateYamlSpecifiedFilterBounds = (fieldExtentsForField, selectedRegionFieldValue, filterBoundsTheButtonsShouldToggleForButton) => {
	if (typeof filterBoundsTheButtonsShouldToggleForButton[0] === 'string') return getSimilarCommunitiesFilterBounds(fieldExtentsForField, selectedRegionFieldValue, filterBoundsTheButtonsShouldToggleForButton)
	
	// Trim bounds to be within extents if needed
	const lowerBound = Math.max(fieldExtentsForField[0], filterBoundsTheButtonsShouldToggleForButton[0])
	const upperBound = Math.min(fieldExtentsForField[1], filterBoundsTheButtonsShouldToggleForButton[1])
	return [lowerBound, upperBound]
}

export const updateFieldsBoundsAfterYamlSpecifiedFilterButtonPress = ({fieldName, filterBoundsTheButtonsShouldToggle}, buttonIndex) => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const fieldExtentsForField = fieldExtentsSelector(reduxState)[fieldName]
		const selectedRegionACSDataRow = selectedRegionACSDataRowSelector(reduxState)
		const selectedRegionFieldValue = selectedRegionACSDataRow[fieldName]
		
		const updatedFieldFilterBounds = {
			[fieldName]: calculateYamlSpecifiedFilterBounds(fieldExtentsForField, selectedRegionFieldValue, filterBoundsTheButtonsShouldToggle[buttonIndex])
		}
		dispatch(updateFieldFilterBounds({ updatedFieldFilterBounds }))
	}
}

export const handleYamlSpecifiedFilterButtonPress = (yamlSpecifiedFieldFilterInfo, buttonIndex) => {
	return (dispatch, getReduxState) => {
		dispatch(toggleButtonToggleStatusArrayForField(yamlSpecifiedFieldFilterInfo, buttonIndex))
		dispatch(updateFieldsBoundsAfterYamlSpecifiedFilterButtonPress(yamlSpecifiedFieldFilterInfo, buttonIndex))
	}
}


// Handles updating field filter bounds AND field extents, whether after a direct change or just a change in data source. Then refreshes the map.
export const updateFieldFilterBounds = (updates = {}) => {
	// Note: updatedFieldFilterBounds only needs to contain the bounds that have changed
	const  {updatedFieldFilterBounds = null, resetToExtents = false, fieldNameDirectlyEdited = ''} = updates
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const fieldExtents = fieldExtentsSelector(reduxState)
		const existingFilterFieldBounds = fieldFilterBoundsSelector(reduxState)
		const fieldFilterBounds = fieldFilterBoundsSelector(reduxState)
		
		// 0. For manual updates, update that we're now doing custom filters
		if (fieldNameDirectlyEdited) {
			dispatch(toggleHasBeenEditedByTheUserProp(fieldNameDirectlyEdited, true))
		}
		
		const ACSDataForStates = ACSDataForStatesSelector(reduxState)
		if (ACSDataForStates.length === 0) {
			return // a temporary hack to prevent extents/filters being overwritten with "undefined/NaN" when we're waiting for new US state data to load
		}

		// 1. PREP: Format fieldExtents into how we store filter bounds
		const fieldFilterBoundsResetToMatchExtents = Object.keys(fieldExtents)
			.reduce((_fieldFilterBoundsResetToMatchExtents, fieldName) => {
				_fieldFilterBoundsResetToMatchExtents[fieldName] = {
					...DEFAULT_FILTER_BOUNDS_OBJECT_PROPERTIES,
					...(fieldFilterBounds[fieldName] ? fieldFilterBounds[fieldName] : {}),
					filterBounds: fieldExtents[fieldName],
					hasBeenEditedByTheUser: false,
				}
				return _fieldFilterBoundsResetToMatchExtents
			}, {})
		

		// 2. Decide what to use as filedFilterBounds: extents, or existing, or merged in
		const _fieldFilterBounds = (resetToExtents && fieldFilterBoundsResetToMatchExtents)
			|| (updatedFieldFilterBounds && mergeInUpdatedFieldBounds(existingFilterFieldBounds, updatedFieldFilterBounds))
			|| (Object.keys(existingFilterFieldBounds).length && existingFilterFieldBounds)
			|| fieldFilterBoundsResetToMatchExtents

		// 3. Update filter bounds as needed:
		const fieldFilterBoundsAdjustedWithinTheExtents = getFieldFilterBoundsAdjustedWithinTheExtents(fieldExtents, _fieldFilterBounds)
		const fieldFilterUpdates = {
			updatedFieldFilterBounds: fieldFilterBoundsAdjustedWithinTheExtents,
			fieldExtents: fieldExtents,
		}

		dispatch(doFieldFilterBoundsUpdateInMapRedux(fieldFilterUpdates))
	}
}


export const initializeCartoACSDataFromURL = (cartoACSDataFromURL = {}) => {
	return (dispatch, getReduxState) => {
		const {selectedUSStatesToFilterWithin} = cartoACSDataFromURL
		const _formattedCartoACSDataFromURL = {
			...cartoACSDataFromURL,
			...(
				selectedUSStatesToFilterWithin
					? {selectedUSStatesToFilterWithin: selectedUSStatesToFilterWithin.filter(onlyUnique)}
					: {}
			)
		}
		dispatch(initializeCartoACSDataFromURLAction(_formattedCartoACSDataFromURL))
	}
}

export const getBoundForStates = reduxState => {
	const selectedUSStatesToFilterWithin = selectedUSStatesToFilterWithinSelector(reduxState)
	const _turfManager = _turfManagerSelector(reduxState)
	return _turfManager && _turfManager.getBoundForStates(selectedUSStatesToFilterWithin)
}

export const addUSStateToFilterWithin = (addedUSState) => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const selectedUSStatesToFilterWithin = selectedUSStatesToFilterWithinSelector(reduxState)
		
		if (selectedUSStatesToFilterWithin.includes(addedUSState))
			return // skip if already exists

		dispatch(addUSStateToFilterWithinAction(addedUSState))
		dispatch(fetchCartoACSSummaryAndDataIfNeeded({geographyOrUSStateChanged: true}))
		
		const bounds = getBoundForStates(reduxState)
		if (bounds)
			dispatch(mapFitBounds(bounds))

		dispatch(_updateURLWithCurrentState())
	}
}

// helper that skips saving and turf fit bounds, etc
export const defaultUSStatesToFilterWithin = (states) => {
	return (dispatch, getReduxState) => {
		states.forEach((state) => {
			dispatch(addUSStateToFilterWithinAction(state))
			dispatch(fetchCartoACSSummaryAndDataIfNeeded())
		})
	}
}
